import { Helmet } from 'react-helmet-async';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Calculator, Home, TrendingUp, Shield, Users, CheckCircle, FileText, MessageSquare, Handshake, ClipboardCheck, Calendar, Phone } from 'lucide-react';
import { LocationSelector } from '@/components/LocationSelector';
import WhatsAppButton from '@/components/WhatsAppButton';
import { openCalendlyPopup } from '@/utils/calendly';
const werkwijzeStappen = [{
  icon: <MessageSquare className="w-8 h-8 text-primary" />,
  title: "Kennismaking",
  description: "We bespreken je wensen, plannen en financiële situatie tijdens een persoonlijk gesprek. Dit eerste gesprek is kosteloos."
}, {
  icon: <FileText className="w-8 h-8 text-primary" />,
  title: "Hypotheekadvies",
  description: "Je ontvangt een uitgebreid adviesrapport met de beste hypotheekopties voor jouw situatie."
}, {
  icon: <ClipboardCheck className="w-8 h-8 text-primary" />,
  title: "Aanvraag regelen",
  description: "Wij regelen de volledige aanvraag en communicatie met de geldverstrekker voor je."
}, {
  icon: <Handshake className="w-8 h-8 text-primary" />,
  title: "Begeleiding tot het einde",
  description: "Van offerte tot sleuteloverdracht blijven we je begeleiden tijdens het hele proces."
}];
const voordelen = ["Gratis hypotheekoriëntatie", "Vergelijking van 40+ geldverstrekkers", "Begeleiding van aanvraag tot afsluiting", "Hypotheekgarantie mogelijk", "Ook voor zelfstandigen en flexwerkers", "Oversluiten naar betere voorwaarden"];
const Hypotheken = () => {
  const scrollToAfspraak = () => {
    const element = document.getElementById('afspraak');
    if (element) {
      const elementPosition = element.offsetTop;
      const offsetPosition = elementPosition - 100; // Account for menu height
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen">
      <Helmet>
        <title>Hypotheekadvies - Compleet Verzekerd</title>
        <meta name="description" content="Gratis hypotheekadvies via De Hypotheekshop. Vergelijking 40+ geldverstrekkers, scherpe rentes, begeleiding A tot Z. 6 vestigingen in de regio!" />
        <meta name="keywords" content="hypotheekadvies, hypotheekshop, gratis hypotheekoriëntatie, hypotheekrente, oversluiten, geldverstrekkers vergelijken, hypotheekgarantie, zelfstandigen hypotheek" />
        <link rel="canonical" href="https://compleetverzekerd.nl/hypotheken/" />
      </Helmet>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded">
        Spring naar hoofdinhoud
      </a>
      
      <Header />
      
      <main id="main-content">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary/10 to-accent/10 py-16 md:py-24">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
                Hypotheekadvies
              </h1>
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">Wij helpen je bij het vinden van de beste hypotheek voor jouw situatie. Gratis advies, scherpe rentes en persoonlijke begeleiding van A tot Z.
              <br /><br />
                De Hypotheekshop. Tijd voor jouw plan!</p>
              <Button onClick={scrollToAfspraak} size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-3">
                Gratis oriëntatie
              </Button>
            </div>
          </div>
        </section>

        {/* Vestigingen Section */}
        <section className="py-16 bg-muted/50">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Onze hypotheekvestigingen
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Compleet Verzekerd werkt samen met De Hypotheekshop voor jouw hypotheekadvies. 
                Bezoek een van onze zes vestigingen in de regio.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[{
              name: "Dordrecht Stadspolders",
              address: "Van Eesterenplein 95",
              postcode: "3315 KT Dordrecht",
              phone: "078 - 220 00 20",
              email: "dordrecht1323@hypotheekshop.nl",
              link: "https://www.hypotheekshop.nl/vestigingen/dordrechtstadspolders/"
            }, {
              name: "Zwijndrecht",
              address: "Hof van Holland 132",
              postcode: "3333 CE Zwijndrecht",
              phone: "078 - 220 00 24",
              email: "zwijndrecht1324@hypotheekshop.nl",
              link: "https://www.hypotheekshop.nl/vestigingen/zwijndrecht/"
            }, {
              name: "Papendrecht",
              address: "Veerweg 139",
              postcode: "3356 LJ Papendrecht",
              phone: "078 - 2200026",
              email: "papendrecht1326@hypotheekshop.nl",
              link: "https://www.hypotheekshop.nl/vestigingen/papendrecht/"
            }, {
              name: "Dordrecht Centrum",
              address: "Johan de Wittstraat 168",
              postcode: "3311 KN Dordrecht",
              phone: "078 - 613 54 33",
              email: "dordrecht1320@hypotheekshop.nl",
              link: "https://www.hypotheekshop.nl/vestigingen/dordrechtcentrum/"
            }, {
              name: "Rotterdam Oostplein",
              address: "Oostplein 570",
              postcode: "3061 CZ Rotterdam",
              phone: "010 - 213 69 72",
              email: "rotterdam1240@hypotheekshop.nl",
              link: "https://www.hypotheekshop.nl/vestigingen/rotterdamoostplein/"
            }, {
              name: "Gorinchem",
              address: "Havendijk 2",
              postcode: "4201 XB Gorinchem",
              phone: "0183 - 893 010",
              email: "gorinchem1295@hypotheekshop.nl",
              link: "https://www.hypotheekshop.nl/vestigingen/gorinchem/"
            }].map((vestiging, index) => <Card key={index} className="border-border hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 relative">
                    <div className="absolute top-4 right-4">
                      <img src="/assets/logo-hypotheekshop.webp" alt="De Hypotheekshop logo" className="w-12 h-12 object-contain" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-3 pr-16">
                      <a href={vestiging.link} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        {vestiging.name}
                      </a>
                    </h3>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <p>{vestiging.address}</p>
                      <p>{vestiging.postcode}</p>
                      <p><a href={`tel:${vestiging.phone.replace(/[^\d+]/g, '')}`} className="text-primary hover:underline">{vestiging.phone}</a></p>
                      <p><a href={`mailto:${vestiging.email}`} className="text-primary hover:underline">{vestiging.email}</a></p>
                    </div>
                  </CardContent>
                </Card>)}
            </div>
          </div>
        </section>

        {/* Onze werkwijze Section */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Onze werkwijze
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Stap voor stap begeleiden wij je naar de perfecte hypotheek. Persoonlijk en zonder verrassingen.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {werkwijzeStappen.map((stap, index) => <Card key={index} className="h-full border-border hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <div className="mb-4">{stap.icon}</div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">{stap.title}</h3>
                    <p className="text-muted-foreground text-sm">{stap.description}</p>
                  </CardContent>
                </Card>)}
            </div>
            <div className="text-center mb-12 mt-12">
              <p className="text-muted-foreground max-w-2xl mx-auto text-base">We nemen altijd ruim de tijd voor onze adviesgesprekken. We vinden het namelijk belangrijk dat onze klanten begrijpen wat de gevolgen zijn van de keuzes die ze maken.<br />
Door een stapje extra te zetten blijkt vaak dat er nét even iets meer mogelijk is. Of het nu is voor iemand die gaat scheiden en die graag de woning wil behouden. Of voor een ondernemer die nog niet zo lang voor zichzelf werkt. Of 'gewoon' voor iemand die zijn eerste eigen woning koopt. Wij luisteren naar onze klanten. En we onderzoeken altijd álle mogelijkheden. Zodat we voor jou de beste oplossing kunnen bieden.<br /><br />
Tijdens een kennismaking horen we graag van jou wat je plannen zijn. Je krijgt dan van ons meteen al een antwoord op veel van je vragen. Vaak maken we ook meteen al wat berekeningen zodat je alvast een inschatting kan maken van je maandlasten. Aan de hand van dit gesprek kunnen we aangeven wat de haalbaarheid is van je plannen. De Hypotheekshop, tijd voor jouw plan!</p>
            </div>
          </div>
        </section>
        
        {/* Process Section */}
        
        
        {/* Hypotheek Types */}
        

        {/* Benefits Section */}
        <section className="py-16 bg-muted/50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Waarom kiezen voor De Hypotheekshop?</h2>
                <p className="text-lg text-muted-foreground">Ruim 25 jaar ervaring in hypotheken en financieringen</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {voordelen.map((voordeel, index) => <div key={index} className="flex items-center">
                    <Shield className="w-5 h-5 text-primary mr-3 flex-shrink-0" />
                    <span className="text-muted-foreground">{voordeel}</span>
                  </div>)}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="afspraak" className="py-16 bg-primary text-primary-foreground">
          <div className="container mx-auto px-6 text-center">
            <Users className="w-16 h-16 mx-auto mb-6 opacity-80" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Klaar voor je droomhuis?
            </h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">Maak een afspraak voor gratis oriëntatiegesprek. Wij zorgen dat je de beste deal krijgt.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <LocationSelector type="appointment">
                <Button variant="outline-inverse" size="lg">
                  {/* <Calendar className="h-5 w-5" /> */}
                  Afspraak maken bij De Hypotheekshop
                </Button>
              </LocationSelector>
              <LocationSelector type="call">
                <Button variant="secondary" size="lg" className="bg-background text-foreground hover:bg-background/90">
                  {/* <Phone className="h-5 w-5" /> */}
                  Bel De Hypotheekshop
                </Button>
              </LocationSelector>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
      <WhatsAppButton />
    </div>;
};
export default Hypotheken;